// Dagens par: hvad en almindelig spiller får ud af dagens vagt. Ingen server: reservebotten
// ('average') spiller dagens bane, frø og regel igennem i den samme deterministiske sim, så alle,
// der spiller samme dag på samme version, ser det samme par. Et gennemløb tager omkring et halvt
// til et helt sekund, så browseren regner det i små bidder mellem billederne.

import type { DailyPlan } from '../content/daily';
import { createState, step } from '../sim';
import type { Intent, State } from '../sim';
import { autoplayBrain } from './autoplay';

export interface Par {
  won: boolean;
  /** 0 hvis botten tabte. */
  stars: number;
  /** Grøn strøm i procent, afrundet. */
  green: number;
}

/** Højst så mange ticks, før vi giver op (en bane er ca. 3.400). */
const MAX_TICKS = 30_000;

function runner(plan: DailyPlan): { state: State; tick(): boolean } {
  const state = createState(plan.level, plan.difficulty, plan.seed, { mod: plan.mod });
  const brain = autoplayBrain('average', plan.seed);
  let seq = 0;
  let n = 0;
  return {
    state,
    /** Ét tick. Sand, mens banen stadig kører. */
    tick() {
      if (state.phase === 'won' || state.phase === 'lost' || n >= MAX_TICKS) return false;
      const it = brain(state);
      const intents: Intent[] = it ? [{ ...it, clientSeq: ++seq }] : [];
      step(state, intents);
      n++;
      return true;
    },
  };
}

function outcome(state: State): Par {
  const r = state.result;
  const won = !!r?.won;
  return { won, stars: won ? (r?.stars ?? 0) : 0, green: Math.round(r?.greenPct ?? 0) };
}

/** Synkront: til test og node. */
export function parOutcome(plan: DailyPlan): Par {
  const r = runner(plan);
  while (r.tick()) {
    /* kør banen færdig */
  }
  return outcome(r.state);
}

const cache = new Map<string, Promise<Par>>();

/** Asynkront i bidder på ca. 6 ms, så spillet ikke hakker. Huskes pr. dag. */
export function dailyPar(plan: DailyPlan): Promise<Par> {
  const hit = cache.get(plan.key);
  if (hit) return hit;
  const p = new Promise<Par>((resolve) => {
    const r = runner(plan);
    // setTimeout, ikke requestIdleCallback: spillet tegner hvert billede, så der er sjældent "idle",
    // og parret kom først efter 15 s. 6 ms ad gangen efterlader det meste af hvert billede til spillet.
    const later = (fn: () => void) => setTimeout(fn, 0);
    const chunk = () => {
      const t0 = performance.now();
      while (performance.now() - t0 < 6) {
        if (!r.tick()) {
          resolve(outcome(r.state));
          return;
        }
      }
      later(chunk);
    };
    later(chunk);
  });
  cache.set(plan.key, p);
  return p;
}

/** Spillerens resultat mod parret: stjerner først, så grøn strøm. */
export function vsPar(you: Par, par: Par): 'over' | 'lige' | 'under' {
  const a = you.won ? you.stars : 0;
  const b = par.won ? par.stars : 0;
  if (a !== b) return a > b ? 'over' : 'under';
  if (!you.won && !par.won) return 'lige';
  if (you.green !== par.green) return you.green > par.green ? 'over' : 'under';
  return 'lige';
}
